import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';

interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

export default function AdminUsers({ adminUser }: any) {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminRole, setNewAdminRole] = useState('READ_ONLY');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/audit-logs/admins`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAdmins(data.data);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminEmail || !newAdminEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSaving(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/admin-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: newAdminEmail.toLowerCase().trim(),
          role: newAdminRole,
        }),
      });

      if (response.ok) {
        toast.success('Admin user added successfully');
        setShowAddModal(false);
        setNewAdminEmail('');
        setNewAdminRole('READ_ONLY');
        fetchAdmins();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to add admin user');
      }
    } catch (error) {
      toast.error('Error adding admin user');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/admin-users/${id}/${currentStatus ? 'deactivate' : 'activate'}`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success(`Admin ${currentStatus ? 'deactivated' : 'activated'} successfully`);
        fetchAdmins();
      } else {
        toast.error('Failed to update admin status');
      }
    } catch (error) {
      toast.error('Error updating admin status');
    }
  };

  const handleUpdateRole = async (id: string, newRole: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/admin-users/${id}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole }),
      });

      if (response.ok) {
        toast.success('Admin role updated successfully');
        fetchAdmins();
      } else {
        toast.error('Failed to update admin role');
      }
    } catch (error) {
      toast.error('Error updating admin role');
    }
  };

  const cardStyle = {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN': return '#ef4444';
      case 'SUPPORT': return '#3b82f6';
      case 'BILLING': return '#10b981';
      case 'READ_ONLY': return '#6b7280';
      default: return '#6b7280';
    }
  };

  return (
    <>
      <Navigation adminUser={adminUser} />
      <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto', background: '#f9fafb', minHeight: 'calc(100vh - 64px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: 0 }}>
            Admin Users
          </h1>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              padding: '12px 24px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            + Add Admin User
          </button>
        </div>

        <div style={cardStyle}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              Loading admin users...
            </div>
          ) : admins.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
              No admin users found
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                      Name
                    </th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                      Email
                    </th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                      Role
                    </th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                      Status
                    </th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                      Created
                    </th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '12px 8px', color: '#111827', fontWeight: '500' }}>
                        {admin.displayName}
                      </td>
                      <td style={{ padding: '12px 8px', color: '#6b7280', fontSize: '14px' }}>
                        {admin.email}
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <select
                          value={admin.role}
                          onChange={(e) => handleUpdateRole(admin.id, e.target.value)}
                          disabled={admin.email === adminUser?.email}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: `${getRoleColor(admin.role)}20`,
                            color: getRoleColor(admin.role),
                            cursor: admin.email === adminUser?.email ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                          <option value="SUPPORT">SUPPORT</option>
                          <option value="BILLING">BILLING</option>
                          <option value="READ_ONLY">READ_ONLY</option>
                        </select>
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: admin.isActive ? '#d1fae5' : '#fee2e2',
                          color: admin.isActive ? '#065f46' : '#991b1b',
                        }}>
                          {admin.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 8px', color: '#6b7280', fontSize: '14px' }}>
                        {new Date(admin.createdAt).toLocaleDateString('da-DK')}
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <button
                          onClick={() => handleToggleActive(admin.id, admin.isActive)}
                          disabled={admin.email === adminUser?.email}
                          style={{
                            padding: '6px 12px',
                            background: admin.email === adminUser?.email ? '#f3f4f6' : (admin.isActive ? '#fee2e2' : '#d1fae5'),
                            color: admin.email === adminUser?.email ? '#9ca3af' : (admin.isActive ? '#991b1b' : '#065f46'),
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: admin.email === adminUser?.email ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {admin.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add Admin Modal */}
        {showAddModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '500px',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>
                Add Admin User
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
                Add a user from the Virtue tenant as an admin. They must sign in with their Virtue Microsoft 365 account.
              </p>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                Email Address (from virtue.dk):
              </label>
              <input
                type="email"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                placeholder="user@virtue.dk"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '16px',
                }}
              />
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                Role:
              </label>
              <select
                value={newAdminRole}
                onChange={(e) => setNewAdminRole(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '20px',
                }}
              >
                <option value="READ_ONLY">READ_ONLY - Can only view data</option>
                <option value="SUPPORT">SUPPORT - Can extend trials, sync data</option>
                <option value="BILLING">BILLING - Can manage billing and revenue</option>
                <option value="SUPER_ADMIN">SUPER_ADMIN - Full access</option>
              </select>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowAddModal(false); setNewAdminEmail(''); }}
                  style={{
                    padding: '10px 16px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddAdmin}
                  disabled={saving}
                  style={{
                    padding: '10px 16px',
                    background: saving ? '#9ca3af' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                  }}
                >
                  {saving ? 'Adding...' : 'Add Admin'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
